<template>
  <div
    v-if="loading"
    class="print-pdf unit container edit-user-unit-container hideonprint"
  >
    <div class="contentSection section-pdf-container pt-8 pa-10 text-center">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        width="4"
      />
      <p class="mt-6 text-body-1">{{ $t('coursePdf.loading') }}</p>
    </div>
  </div>
  <div
    v-else-if="loadError"
    class="print-pdf unit container edit-user-unit-container"
  >
    <div class="contentSection section-pdf-container pt-8 pa-4">
      <v-alert type="error" variant="tonal">{{ loadError }}</v-alert>
    </div>
  </div>
  <div
    v-else-if="currentCourse.length > 0"
    class="print-pdf unit container edit-user-unit-container"
    :class="currentCourse[0].baseYear"
  >
    <div class="pdf-background-img-container">
      <div class="pdf-background-image"></div>
    </div>
    <div class="contentSection section-pdf-container pt-8">
      <div class="hideonprint">
        <v-alert border="start" color="rgb(165, 216, 103)"
          ><h4>{{ $t('coursePdf.instructionsTitle') }}</h4>
          <p>
            {{ $t('coursePdf.instructionsStep1') }}
          </p>
          <p>{{ $t('coursePdf.instructionsStep2') }}</p>
          <p>{{ $t('coursePdf.instructionsStep3') }}</p>
          <h4>{{ $t('coursePdf.highlightsTitle') }}</h4>
          <p>{{ $t('coursePdf.highlightsStep') }}</p>
        </v-alert>
        <v-btn
          variant="outlined"
          target="_blank"
          class="unit-icon mt-4 mb-10"
          :class="currentCourse[0].baseYear"
          @click="printPDF()"
        >
          {{ $t('coursePdf.downloadButton') }}
        </v-btn>
      </div>
      <h1 id="course-title">{{ courseTitle }}</h1>
      <div v-if="sectionsData.length">
        <div v-for="(sec, si) in sectionsData" :key="sec.id">
          <h2
            v-if="sec.title"
            :id="'section-title-' + sec.id"
            class="text-h5 mt-10 mb-6"
            :class="{ 'break-page-before': si > 0 }"
          >
            {{ sec.title }}
          </h2>
          <div v-for="(u, ui) in sec.units" :key="u.id">
            <div
              class="unit-meta-container"
              :class="{ 'break-page-before': si > 0 || ui > 0 }"
            >
            <div class="mb-0 mt-4">
              <h2
                id="unit-title"
                class="base-year"
                :class="currentCourse[0].baseYear"
                v-html="u.title"
              ></h2>
            </div>
            <div class="mb-7 mt-0">
              <span class="font-weight-bold" v-html="u.subtitle"></span>
            </div>
            </div>

            <v-flex xs12>
              <span v-if="u.listen || u.complete || u.continueLearning">
                <v-divider class="dashed my-10"></v-divider>

                <div id="start" class="font-weight-bold px-10 mb-3">
                  {{ $t('unitStudy.sections.assignments') }}
                </div>
                <div class="unit-assignments px-10 mb-10">
                  <div v-if="u.listen">
                    <div class="flex-this mb-8">
                      <div v-if="!onLine" class="flex-this align-baseline">
                        <input
                          type="checkbox"
                          :id="'listen-checkbox-' + u.id"
                          disabled
                          v-model="u._pdfListenConfirmed"
                          class="mt-0 pt-0 mr-3"
                        />
                        <div class="flex-this" style="margin-top: 2px">
                          <span class="font-weight-bold"
                            >{{
                              (u.listenLabel && u.listenLabel.toUpperCase()) ||
                              'LISTEN'
                            }}:</span
                          >
                        </div>
                      </div>
                      <div v-else>
                        <v-checkbox
                          v-model="u._pdfListenConfirmed"
                          hide-details
                          disabled
                          class="mt-0 pt-0"
                        >
                          <template #label>
                            <div class="flex-this" style="margin-top: 2px">
                              <span class="font-weight-bold"
                                >{{
                                  (u.listenLabel &&
                                    u.listenLabel.toUpperCase()) ||
                                  'LISTEN'
                                }}:</span
                              >
                            </div>
                          </template>
                        </v-checkbox>
                      </div>
                      <span v-html="u.listen" class="label-text"></span>
                    </div>
                  </div>
                  <div v-if="u.complete">
                    <div class="flex-this mb-3">
                      <div v-if="!onLine" class="flex-this align-baseline">
                        <input
                          type="checkbox"
                          :id="'complete-checkbox-' + u.id"
                          disabled
                          v-model="u._pdfCompleteConfirmed"
                          class="mt-0 pt-0 mr-3"
                        />
                        <div class="flex-this" style="margin-top: 2px">
                          <span class="font-weight-bold"
                            >{{
                              (u.completeLabel &&
                                u.completeLabel.toUpperCase()) ||
                              'COMPLETE'
                            }}:</span
                          >
                        </div>
                      </div>
                      <div v-else>
                        <v-checkbox
                          v-model="u._pdfCompleteConfirmed"
                          hide-details
                          disabled
                          class="mt-0 pt-0"
                        >
                          <template #label>
                            <div class="flex-this" style="margin-top: 2px">
                              <span class="font-weight-bold"
                                >{{
                                  (u.completeLabel &&
                                    u.completeLabel.toUpperCase()) ||
                                  'COMPLETE'
                                }}:</span
                              >
                            </div>
                          </template>
                        </v-checkbox>
                      </div>
                      <span v-html="u.complete" class="label-text"></span>
                    </div>
                  </div>
                  <div v-if="u.continueLearning">
                    <div class="flex-this">
                      <div v-if="!onLine" class="flex-this align-baseline">
                        <input
                          type="checkbox"
                          :id="'continue-checkbox-' + u.id"
                          disabled
                          v-model="u._pdfContinueConfirmed"
                          class="mt-0 pt-0 mr-3"
                        />
                        <div class="flex-this" style="margin-top: 2px">
                          <span class="font-weight-bold"
                            >{{
                              (u.continueLearningLabel &&
                                u.continueLearningLabel.toUpperCase()) ||
                              'CONTINUE'
                            }}:</span
                          >
                        </div>
                      </div>
                      <div v-else>
                        <v-checkbox
                          v-model="u._pdfContinueConfirmed"
                          hide-details
                          disabled
                          class="mt-0 pt-0"
                        >
                          <template #label>
                            <div class="flex-this" style="margin-top: 2px">
                              <span class="font-weight-bold"
                                >{{
                                  (u.continueLearningLabel &&
                                    u.continueLearningLabel.toUpperCase()) ||
                                  'CONTINUE'
                                }}:</span
                              >
                            </div>
                          </template>
                        </v-checkbox>
                      </div>
                      <span
                        v-html="u.continueLearning"
                        class="label-text"
                      ></span>
                    </div>
                  </div>
                </div>
              </span>

              <div id="truth" class="_break-page-before">
              <div class="flex-container mt-5">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.truth') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <UserVersesQuotes
                :content="u.truthVersesQuotes"
                section="truthVersesQuotes"
              />

              <v-divider v-if="u.truthVersesQuotes" class="dashed"></v-divider>

              <div
                v-if="u.truthInBible"
                v-html="u.truthInBible"
                class="_print-content-container mb-7 mt-12"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.truthStudyQuestions"
                :unitID="u.id"
                :sectionID="sec.id"
                status="archived"
                print="pdf"
                section="truthStudyQuestions"
                @has-loaded="contentHasLoaded"
              />

              <div
                v-if="u.truthBodyContent"
                v-html="u.truthBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>

            <div id="equip" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.equipping') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.equipBodyContent"
                v-html="u.equipBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.equipStudyQuestions"
                :unitID="u.id"
                :sectionID="sec.id"
                status="archived"
                print="pdf"
                section="equipStudyQuestions"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="application" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.accountability') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.accountBodyContent"
                v-html="u.accountBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.accountStudyQuestions"
                :unitID="u.id"
                :sectionID="sec.id"
                status="archived"
                print="pdf"
                section="accountStudyQuestions"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="mission" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.mission') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-if="u.missionBodyContent"
                v-html="u.missionBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.missionStudyQuestions"
                :unitID="u.id"
                :sectionID="sec.id"
                status="archived"
                print="pdf"
                section="missionStudyQuestions"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div id="supplication" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.supplication') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div v-if="u.supplicationverse" class="my-12 text-center">
                <div v-html="u.supplicationverse"></div>
                <div
                  v-html="u.supplicationreference"
                  class="mt-3 mb-12 font-weight-bold"
                ></div>

                <v-divider class="dashed"></v-divider>
              </div>

              <div
                v-if="u.supplicationBodyContent"
                v-html="u.supplicationBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>

              <UserStudyQuestions
                :unitbackdoor="unitbackdoor"
                :backdoor="backdoor"
                :content="u.supplicationStudyQuestions"
                :unitID="u.id"
                :sectionID="sec.id"
                status="archived"
                print="pdf"
                section="supplicationStudyQuestions"
                @has-loaded="contentHasLoaded"
              />
            </div>

            <div
              v-if="u.unitNotes"
              id="usernotes"
              class="break-page-before break-page-after"
            >
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.userNotes') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-html="u.unitNotes"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>

            <div v-if="u.footnotesBodyContent" class="break-page-before">
              <div class="flex-container">
                <div
                  class="font-weight-bold text-h6 base-year"
                  :class="currentCourse[0].baseYear"
                >
                  {{ $t('unitStudy.sections.footnotes') }}
                </div>
                <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              </div>

              <div
                v-html="u.footnotesBodyContent"
                class="_print-content-container mb-7 mt-3"
              ></div>
            </div>
          </v-flex>
          </div>
        </div>
      </div>
      <div v-else>{{ $t('coursePdf.noContent') }}</div>
    </div>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { scopeToChurch } from '@/helpers/churchScope'
  import firebase from 'firebase'
  import axios from 'axios'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import UserStudyQuestions from '@/components/utilities/UserStudyQuestions'
  import UserVersesQuotes from '@/components/utilities/UserVersesQuotes'
  import { assignUnitStudyQuestionNumbers } from '@/helpers/assignUnitStudyQuestionNumbers'
  import {
    applyLocalizedWeekFields,
    resolveLocalizedWeek,
  } from '@/helpers/localizeWeekContent'
  // import UserEditUnit from '@/components/utilities/UserEditUnit'

  export default {
    name: 'CoursePdf',
    components: {
      UserStudyQuestions,
      UserVersesQuotes,
    },
    data() {
      return {
        feedback: null,
        snackbar: true,
        sectionsData: [],
        currentCourse: [],
        unitbackdoor: 0,
        backdoor: 0,
        onLine: navigator.onLine,
        courseTitle: '',
        commentsArray: [],
        loading: false,
        loadError: null,
      }
    },
    watch: {
      '$route.params.courseID': {
        handler: function (id) {
          if (id) this.init()
        },
        deep: true,
        immediate: true,
      },
    },
    computed: {
      ...mapState('UserData', ['user']),
      ...mapState('AppData', ['userActiveCourse']),
      ...mapGetters('AppData', ['getCourse', 'getActiveCourse']),
      ...mapGetters('UserData', ['getRoles']),
    },
    mounted() {
      window.addEventListener('online', this.updateOnlineStatus)
      window.addEventListener('offline', this.updateOnlineStatus)
    },
    beforeUnmount() {
      window.removeEventListener('online', this.updateOnlineStatus)
      window.removeEventListener('offline', this.updateOnlineStatus)
    },
    methods: {
      contentHasLoaded(payload) {
        // once the study questions have loaded create a div
        this.loadingComplete = payload.loadingComplete
        // console.log('1 ', this.loadingComplete)
        let loadedDiv = document.createElement('div')
        loadedDiv.setAttribute('class', 'content-has-loaded')
        document.body.appendChild(loadedDiv)
      },
      updateOnlineStatus(e) {
        const { type } = e
        this.onLine = type === 'online'
      },
      printPDF() {
        // window.print();
        // return false;
        try {
          if (!document.execCommand('print', false, null)) {
            window.print()
          }
        } catch {
          window.print()
        }
      },
      async fetchVisibleSectionsMap() {
        const roles = this.getRoles || []
        const userCid = this.user?.cid
        const isSuper =
          Array.isArray(roles) &&
          (roles.includes('superAdmin') || roles.includes('helpDesk'))
        const snap = await db.collection('sections').get()
        const map = new Map()
        snap.forEach((doc) => {
          const s = doc.data()
          s.id = doc.id
          const canView = isSuper
            ? true
            : (s.type === 'public' && s.status === 'verified') ||
              (s.cid && userCid && s.cid === userCid)
          if (canView) map.set(doc.id, s)
        })
        return map
      },
      async fetchUnitsByIds(ids) {
        const map = new Map()
        const unique = [...new Set(ids.filter(Boolean))]
        if (!unique.length) return map
        const FieldPath = firebase.firestore.FieldPath
        for (let i = 0; i < unique.length; i += 10) {
          const chunk = unique.slice(i, i + 10)
          const snap = await db
            .collection('units')
            .where(FieldPath.documentId(), 'in', chunk)
            .get()
          snap.forEach((doc) => {
            map.set(doc.id, { ...doc.data(), id: doc.id })
          })
        }
        return map
      },
      async resolveSectionsForPdf(coursePayload) {
        let sections = Array.isArray(coursePayload.sections)
          ? coursePayload.sections
          : []

        const sectionMissingUnits = (sec) => {
          const u = sec.units
          if (!Array.isArray(u) || u.length === 0) return true
          return u.every(
            (unit) =>
              unit &&
              typeof unit === 'object' &&
              !unit.title &&
              !unit.truthBodyContent &&
              !unit.truthInBible &&
              Object.keys(unit).length <= 2
          )
        }

        const needsHydration =
          sections.length > 0 && sections.some(sectionMissingUnits)

        if (needsHydration) {
          const sectionMap = await this.fetchVisibleSectionsMap()
          let merged = sections.map((s) => {
            const full = sectionMap.get(s.id)
            return full ? { ...s, ...full, id: s.id } : s
          })
          const unitIds = merged.flatMap((sec) =>
            (sec.units || []).map((u) => u && u.id).filter(Boolean)
          )
          const unitMap = await this.fetchUnitsByIds(unitIds)
          merged = merged.map((sec) => ({
            ...sec,
            units: (sec.units || []).map((stub) => {
              if (!stub || typeof stub !== 'object') return stub
              const full = unitMap.get(stub.id)
              return full ? { ...stub, ...full, id: stub.id } : stub
            }),
          }))
          return merged
        }

        return sections
      },
      async fetchSectionTitle(sectionID) {
        const docRef = db.collection('sections').doc(sectionID)
        const doc = await docRef.get()
        if (doc.exists) return doc.data().title || ''
        return ''
      },
      async init() {
        this.backdoor++
        this.sectionsData = []
        this.currentCourse = []
        this.commentsArray = []
        this.unitbackdoor++
        this.loadError = null
        this.loading = true

        const courseID = this.$route.params.courseID
        if (!courseID) {
          this.loadError = this.$t('coursePdf.loadErrorNoCourseId')
          this.loading = false
          return
        }

        try {
          let currentCourseRef = db.collection('currentcourses')
          currentCourseRef = currentCourseRef.where('storageID', '==', courseID)
          currentCourseRef = scopeToChurch(currentCourseRef, this.$store)
          let activeRef = await currentCourseRef.get()

          if (activeRef.empty) {
            this.loadError = this.$t('coursePdf.loadErrorNotFound')
            return
          }

          const doc = activeRef.docs[0]
          let courseMeta = doc.data()
          courseMeta.id = doc.id

          const storage = firebase.storage()
          const storageRef = storage.ref()
          const objectRef = storageRef.child(courseMeta.storageRef)
          let url = await objectRef.getDownloadURL()
          let resp = await axios.get(url)

          const missionRef = firebase.storage().ref().child(courseMeta.missions)
          let missionURL = await missionRef.getDownloadURL()
          let missionResp = await axios.get(missionURL)

          const coursePayload = resp.data
          coursePayload.missions = missionResp.data

          const sections = await this.resolveSectionsForPdf(coursePayload)
          const built = []
          const defaultLanguage = coursePayload.requiredLanguage || 'en'

          for (const section of sections) {
            const sectionID = section.id
            const requiredLanguage =
              section.requiredLanguage || defaultLanguage || 'en'
            const title = await this.fetchSectionTitle(sectionID)
            const unitsOut = []

            for (const unitRow of section.units || []) {
              const u = { ...unitRow }
              u._pdfListenConfirmed = false
              u._pdfCompleteConfirmed = false
              u._pdfContinueConfirmed = false

              if (coursePayload.missions) {
                const missionList = Array.isArray(coursePayload.missions)
                  ? coursePayload.missions
                  : []
                const currentWeek = missionList.filter(
                  (item) => item.unitID == u.id
                )
                if (currentWeek.length) {
                  const localizedWeek = await resolveLocalizedWeek(
                    db,
                    currentWeek[0],
                    requiredLanguage
                  )
                  applyLocalizedWeekFields(u, localizedWeek, currentWeek[0])
                }
              }

              let notesSnap
              try {
                let currentCourseContentRef = db.collection('coursecontent')
                currentCourseContentRef = currentCourseContentRef.where(
                  'courseID',
                  '==',
                  courseID
                )
                currentCourseContentRef = currentCourseContentRef.where(
                  'sectionID',
                  '==',
                  sectionID
                )
                currentCourseContentRef = currentCourseContentRef.where(
                  'unitID',
                  '==',
                  u.id
                )
                notesSnap = await currentCourseContentRef
                  .orderBy('unitNotes')
                  .get()
              } catch (notesErr) {
                let currentCourseContentRef = db.collection('coursecontent')
                currentCourseContentRef = currentCourseContentRef.where(
                  'courseID',
                  '==',
                  courseID
                )
                currentCourseContentRef = currentCourseContentRef.where(
                  'sectionID',
                  '==',
                  sectionID
                )
                currentCourseContentRef = currentCourseContentRef.where(
                  'unitID',
                  '==',
                  u.id
                )
                notesSnap = await currentCourseContentRef.get()
              }

              if (!notesSnap.empty) {
                const notesdoc = notesSnap.docs[0]
                let note = notesdoc.data()
                note.id = notesdoc.id
                let cleanParagraphs = note.unitNotes.replace(/<br>\\*/g, '')
                u.unitNotes = cleanParagraphs
              }

              const numberedUnit = assignUnitStudyQuestionNumbers(u)
              unitsOut.push(numberedUnit)
              await this.getContent(courseID, sectionID, numberedUnit)
            }

            built.push({ id: sectionID, title, units: unitsOut })
          }

          this.courseTitle = coursePayload.title || ''
          this.currentCourse.push(coursePayload)
          this.sectionsData = built
          this.$nextTick(() => this.commentHandler())
        } catch (err) {
          console.error('CoursePdf init', err)
          this.$store.dispatch('AppData/logError', {
            message: 'CoursePdf failed to load',
            stack: err.stack || String(err),
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'CoursePdf.vue',
            function: 'init',
            additionalInfo: {
              courseID: this.$route.params.courseID,
            },
          })
          this.loadError = this.$t('coursePdf.loadErrorGeneric')
        } finally {
          this.loading = false
        }
      },
      getContent(courseID, sectionID, unitObj) {
        return new Promise((resolve) => {
          var coursecontentRef = db.collection('coursecontent')
          coursecontentRef = coursecontentRef.where('courseID', '==', courseID)
          coursecontentRef = coursecontentRef.where(
            'sectionID',
            '==',
            sectionID
          )
          coursecontentRef = coursecontentRef.where('unitID', '==', unitObj.id)
          coursecontentRef
            .get()
            .then((snapshot) => {
              if (!snapshot.empty) {
                snapshot.forEach((doc) => {
                  let uu = doc.data()
                  if (
                    uu.listenConfirmed ||
                    uu.completeConfirmed ||
                    uu.continueConfirmed
                  ) {
                    uu.id = doc.id
                    unitObj._pdfListenConfirmed = !!uu.listenConfirmed
                    unitObj._pdfCompleteConfirmed = !!uu.completeConfirmed
                    unitObj._pdfContinueConfirmed = !!uu.continueConfirmed
                  }
                  if (uu.paragraphID && uu.paragraphID !== null) {
                    let regex = new RegExp(
                      /<([^\s]+).*?id="([^"]*?)".*?>(.+?)<\/\1>|<img[^>]* src="([^"]*)"[^>]*>/gi
                    )

                    let which = uu.paragraphID.split('-')
                    let arrayOfParagraphs = []
                    if (which[1] == 'tbc' && unitObj.truthBodyContent)
                      arrayOfParagraphs =
                        unitObj.truthBodyContent.match(regex)
                    if (which[1] == 'ibc' && unitObj.truthInBible)
                      arrayOfParagraphs = unitObj.truthInBible.match(regex)
                    if (which[1] == 'ebc' && unitObj.equipBodyContent)
                      arrayOfParagraphs =
                        unitObj.equipBodyContent.match(regex)
                    if (which[1] == 'abc' && unitObj.accountBodyContent)
                      arrayOfParagraphs =
                        unitObj.accountBodyContent.match(regex)
                    if (which[1] == 'mbc' && unitObj.missionBodyContent)
                      arrayOfParagraphs =
                        unitObj.missionBodyContent.match(regex)
                    if (which[1] == 'sbc' && unitObj.supplicationBodyContent)
                      arrayOfParagraphs =
                        unitObj.supplicationBodyContent.match(regex)
                    if (which[1] == 'fbc' && unitObj.footnotesBodyContent)
                      arrayOfParagraphs =
                        unitObj.footnotesBodyContent.match(regex)

                    if (!arrayOfParagraphs || !arrayOfParagraphs.length) {
                      return
                    }

                    let searchterm = 'id="' + uu.paragraphID + '"'

                    for (var i = 0; i < arrayOfParagraphs.length; i++) {
                      if (arrayOfParagraphs[i].search(searchterm) != -1) {
                        arrayOfParagraphs[i] =
                          '<p id="' +
                          uu.paragraphID +
                          '">' +
                          uu.paragraph +
                          '</p>'
                      }
                    }
                    if (which[1] == 'tbc')
                      unitObj.truthBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'ibc')
                      unitObj.truthInBible = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'ebc')
                      unitObj.equipBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'abc')
                      unitObj.accountBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'mbc')
                      unitObj.missionBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'sbc')
                      unitObj.supplicationBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                    if (which[1] == 'fbc')
                      unitObj.footnotesBodyContent = arrayOfParagraphs
                        .join('')
                        .toString()
                  }

                  if (uu.commentParentID && uu.commentParentID !== null) {
                    this.commentsArray.push(uu)
                  }
                })
              }
            })
            .then(() => resolve())
        })
      },
      commentHandler() {
        // console.log('commentHandler')

        let hasCommentContainer =
          document.getElementsByClassName('comment-container')
        while (hasCommentContainer[0]) {
          hasCommentContainer[0].parentNode.removeChild(hasCommentContainer[0])
        }
        let hasCommentIcon = document.getElementsByClassName(
          'comment-icon-container'
        )
        while (hasCommentIcon[0]) {
          hasCommentIcon[0].parentNode.removeChild(hasCommentIcon[0])
        }

        // console.log('hasDiv ', hasDiv)
        /** add commenting module to each parapgraph **/
        let allParapgraphs = document.querySelectorAll('p')
        allParapgraphs.forEach((doc) => {
          if (doc.id && doc.textContent) {
            // add class to the paragraph
            doc.className = 'allow-commenting'

            // create a div
            var div = document.createElement('div')

            // add a class to the div
            div.className = 'comment-container'
            // console.log('1 ', this.commentsArray)
            const theComment = this.commentsArray.filter((el) => {
              if (el.commentParentID === doc.id) return el.comment

              return
            })

            // add the match to the div
            if (theComment.length > 0) {
              // add the comment inside the div
              let textNode = document.createTextNode(theComment[0].comment)
              div.append(textNode)

              // show the comment
              doc.classList.add('active')
            }

            // insert div at the beginning of the paragraph
            doc.insertBefore(div, doc.firstChild)
          }
        })
        /** . commenting module **/
      },
    },
  }
</script>

<style scoped>
  [v-cloak] {
    display: none;
  }
  /* .unit h2 {
    font-size: .95em;
} */

  @media print {
    #truth
      :deep(p, )
      #equip
      :deep(p, )
      #application
      :deep(p, )
      #mission
      :deep(p, )
      #supplication
      :deep(p, )
      #truth
      :deep(img, )
      #equip
      :deep(img, )
      #application
      :deep(img, )
      #mission
      :deep(img, )
      #supplication
      :deep(img) {
      page-break-before: auto;
      page-break-after: auto;
      break-before: auto;
      break-after: auto;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      padding-top: 16px;
      padding-bottom: 12px;
      margin-bottom: 0px;
    }
    .break-page-before {
      page-break-before: always;
      padding-top: 5mm;
    }
    .break-page-after {
      page-break-after: always;
      padding-top: 5mm;
      /* margin-top: 25mm;
        margin-bottom: 25mm; */
    }
    .print-content-container,
    .study-question-statement {
      page-break-inside: avoid;
      break-inside: avoid;
      display: block;
    }
    .unit.container .pdf-background-image {
      background: #fff !important;
      width: 100% !important;
      height: 100% !important;
    }
    .hideonprint {
      display: none;
    }
  }

  #truth
    :deep(p, )
    #equip
    :deep(p, )
    #application
    :deep(p, )
    #mission
    :deep(p, )
    #supplication
    :deep(p, )
    #truth
    :deep(img, )
    #equip
    :deep(img, )
    #application
    :deep(img, )
    #mission
    :deep(img, )
    #supplication
    :deep(img) {
    page-break-before: auto;
    page-break-after: auto;
    break-before: auto;
    break-after: auto;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    padding-top: 16px;
    padding-bottom: 12px;
    margin-bottom: 0px;
    display: inline-block;
  }

  .print-content-container,
  .study-question-statement {
    page-break-inside: avoid;
    break-inside: avoid;
    display: block;
  }

  .unit.container {
    max-width: 600px;
  }
  .unit.container .background-img-container {
    position: fixed;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }
  .unit.container .pdf-background-image {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    /* background: url(../../assets/mountains_bw.jpg) no-repeat 50% 0; */
    background: #fff;
    background-size: cover;
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    /*filter: grayscale(100%) blur(0px);*/
  }
  .theme--dark .print-pdf {
    color: #000;
  }
  .print-pdf .theme--dark.v-divider.dashed {
    background-image: linear-gradient(
      to right,
      #acacac 33%,
      rgba(255, 255, 255, 0) 0%
    );
  }
  .theme--dark .unit.container .background-image {
    filter: grayscale(100%) blur(0px) invert(1);
  }
  .pdf-background-image-donotuse:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.85);
  }
  .unit .contentSection {
    /* margin-bottom: 50%; */
    /* max-height: 90vh;
    overflow: hidden; */
    position: relative;
  }
  .edit-user-unit-container.blueYear :deep(a) {
    color: #002b45;
  }
  .edit-user-unit-container.redYear :deep(a) {
    color: #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(a) {
    color: #024731;
  }
  .flex-this {
    display: flex;
  }
  .edit-user-unit-container :deep(img) {
    max-width: 100%;
  }
  .unit-assignments :deep(p) {
    margin-bottom: 0;
  }
  .unit-assignments :deep(label) {
    text-transform: none;
  }
  .label-text {
    margin-left: 5px;
    font-size: 13px;
    padding-top: 2px;
  }
  .unit-assignments :deep(.v-input__slot) {
    align-items: start;
  }
  .unit-icon.blueYear,
  .theme--dark .unit-icon.blueYear {
    border: 0px solid #002b45;
    background: #002b45;
    color: #ffffff;
  }
  .unit-icon.greenYear,
  .theme--dark .unit-icon.greenYear {
    border: 0px solid #024731;
    background: #024731;
    color: #fff;
  }
  .unit-icon.redYear,
  .theme--dark .unit-icon.redYear {
    border: 0px solid #6e273d;
    background: #6e273d;
    color: #ffffff;
  }
  .edit-user-unit-container :deep(.comment-container) {
    opacity: 0;
    height: 0;
  }
  .edit-user-unit-container :deep(.active .comment-container) {
    opacity: 1;
    width: auto;
    position: relative;
    top: -5px;
    border-radius: 4px;
    padding: 10px;
    color: #fff;
    height: auto;
  }
  .edit-user-unit-container.blueYear :deep(.comment-container) {
    background: #002b45;
  }
  .edit-user-unit-container.redYear :deep(.comment-container) {
    background: #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(.comment-container) {
    background: #024731;
  }
  .edit-user-unit-container :deep(.active .comment-container:after) {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -5px;
    transform: translateX(-50%);
    width: 0px;
    height: 0px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    /* border-top: 6px solid rgb(56, 57, 53); */
  }
  .edit-user-unit-container.blueYear :deep(.comment-container:after) {
    border-top: 6px solid #002b45;
  }
  .edit-user-unit-container.redYear :deep(.comment-container:after) {
    border-top: 6px solid #6e273d;
  }
  .edit-user-unit-container.greenYear :deep(.comment-container:after) {
    border-top: 6px solid #024731;
  }
  @media only screen and (max-width: 767px) {
    .flex-this {
      flex-direction: column;
    }
  }

  .print-content-container :deep(img) {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* --- keep comment bubbles + highlights when printing to PDF --- */
  @media print {
    /* Tell the print engine to honor background colors */
    .print-pdf,
    .print-pdf * {
      -webkit-print-color-adjust: exact !important; /* Chrome/Safari */
      print-color-adjust: exact !important; /* Spec */
      color-adjust: exact !important; /* Legacy */
    }

    /* Ensure comment chips are visible/colored in print */
    .print-pdf .comment-container {
      opacity: 1 !important;
      height: auto !important;
    }

    /* Keep the little triangle pointer colored too */
    .print-pdf .comment-container:after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Highlights (adjust selectors to whatever you use) */
    .print-pdf mark,
    .print-pdf .highlight,
    .print-pdf .hl,
    .print-pdf [data-highlight='true'] {
      background-color: #fde68a !important; /* your highlight color */
    }
  }
</style>
